import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Loader2, Calendar, RefreshCw, CheckCircle2, AlertTriangle, Square, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Sezione "Invio Retroattivo (Backfill)" da mostrare nella scheda di modifica immobile.
 *
 * Use case: l'utente registra una nuova casa il 27/04 e vuole inviare retroattivamente i flussi
 * dal 1° marzo (o altra data) fino a ieri. Lo scheduler giornaliero (23:00) inizierà a inviare la
 * giornata corrente da domani; per il pregresso serve un job di backfill esplicito.
 *
 * - Input: data inizio (default: 1° del mese corrente), data fine (default: ieri)
 * - Submit: avvia un job in background, poi pollinga lo stato ogni 4s
 * - Skippa automaticamente i giorni già inviati con success (anti-doppio-invio nel backend)
 */
const Ross1000BackfillSection = ({ propertyId, propertyName }) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  // Default: 1° marzo 2026 — data da cui Holiday World ha iniziato a raccogliere i dati
  const dataCollectionStart = new Date(2026, 2, 1); // mese 0-based: 2 = marzo

  const fmt = (d) => d.toISOString().split('T')[0];

  const [fromDate, setFromDate] = useState(fmt(dataCollectionStart));
  const [toDate, setToDate] = useState(fmt(yesterday));
  const [activeJob, setActiveJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('hw_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Carica l'ultimo job per questa property all'apertura
  const loadRecent = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/flussi-turistici/backfill/recent/${encodeURIComponent(propertyId)}`,
        authHeaders()
      );
      setActiveJob(res.data?.job || null);
    } catch (err) {
      // 403/404 silently ignored (no job yet)
      setActiveJob(null);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  // Polling se il job è in corso
  useEffect(() => {
    if (!activeJob || !['pending', 'running', 'cancelling'].includes(activeJob.status)) return;
    const tick = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API}/flussi-turistici/backfill/status/${activeJob.id}`,
          authHeaders()
        );
        setActiveJob(res.data);
        if (['completed', 'failed', 'cancelled'].includes(res.data?.status)) {
          clearInterval(tick);
          if (res.data.status === 'completed') {
            toast.success(
              `Backfill completato: ${res.data.sent_count} inviati, ${res.data.skipped_count} già presenti, ${res.data.failed_count} falliti`,
              { duration: 8000 }
            );
          } else if (res.data.status === 'cancelled') {
            toast.info('Backfill interrotto: ora puoi riavviarlo dallo stesso periodo');
          } else {
            toast.error(`Backfill fallito: ${res.data.error || 'errore sconosciuto'}`, { duration: 8000 });
          }
        }
      } catch (err) {
        // Ignore polling errors
      }
    }, 4000);
    return () => clearInterval(tick);
  }, [activeJob]);

  const startBackfill = async (rangeOverride = null) => {
    const effectiveFrom = rangeOverride?.fromDate || fromDate;
    const effectiveTo = rangeOverride?.toDate || toDate;
    if (!effectiveFrom || !effectiveTo) {
      toast.error('Seleziona data inizio e fine');
      return;
    }
    if (effectiveFrom > effectiveTo) {
      toast.error('La data inizio deve essere ≤ data fine');
      return;
    }
    const totalDays = Math.floor((new Date(effectiveTo) - new Date(effectiveFrom)) / 86400000) + 1;
    if (!window.confirm(
      `${rangeOverride ? 'Riavvio da zero' : 'Avvio backfill'} ROSS1000 per "${propertyName}":\n\n` +
      `• Periodo: ${effectiveFrom} → ${effectiveTo} (${totalDays} giorni)\n` +
      `• I giorni già inviati con successo verranno saltati automaticamente\n` +
      `• I record con documenti ospiti completi verranno spediti al portale regionale\n\n` +
      `Confermi?`
    )) return;

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/flussi-turistici/backfill/${encodeURIComponent(propertyId)}?from_date=${effectiveFrom}&to_date=${effectiveTo}`,
        {},
        authHeaders()
      );
      toast.success(`Job avviato: ${totalDays} giorni in coda`);
      // Inizia polling
      const newJob = {
        id: res.data.job_id,
        status: 'pending',
        total_days: totalDays,
        from_date: effectiveFrom,
        to_date: effectiveTo,
        processed_days: 0,
        sent_count: 0,
        skipped_count: 0,
        failed_count: 0,
      };
      setActiveJob(newJob);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        toast.error(detail || 'Backfill già in corso per questa property');
        loadRecent();
      } else {
        toast.error(detail || 'Errore avvio backfill');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancelBackfill = async () => {
    if (!activeJob?.id || cancelling) return;
    if (!window.confirm(
      `Interrompere il job ROSS1000 di "${propertyName}"?\n\n` +
      `Verrà fermato solo questo immobile. Gli invii già confermati resteranno validi.`
    )) return;

    setCancelling(true);
    try {
      const res = await axios.post(
        `${API}/flussi-turistici/backfill/${encodeURIComponent(propertyId)}/cancel/${encodeURIComponent(activeJob.id)}`,
        {},
        authHeaders()
      );
      setActiveJob(res.data?.job || { ...activeJob, status: res.data?.status || 'cancelling' });
      toast.info(res.data?.message || 'Interruzione richiesta');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante l’interruzione del job');
    } finally {
      setCancelling(false);
    }
  };

  const restartBackfill = () => {
    const restartFrom = activeJob?.from_date || fromDate;
    const restartTo = activeJob?.to_date || toDate;
    setFromDate(restartFrom);
    setToDate(restartTo);
    startBackfill({ fromDate: restartFrom, toDate: restartTo });
  };

  const isRunning = activeJob && ['pending', 'running', 'cancelling'].includes(activeJob.status);
  const progressPct = activeJob && activeJob.total_days > 0
    ? Math.round((activeJob.processed_days / activeJob.total_days) * 100)
    : 0;

  return (
    <div className="mt-4 p-4 border-2 border-purple-200 bg-purple-50 rounded-lg" data-testid="ross1000-backfill-section">
      <div className="flex items-start gap-2 mb-3">
        <RefreshCw className="h-5 w-5 text-purple-700 mt-0.5 shrink-0" />
        <div>
          <h4 className="font-semibold text-purple-900">Invio Retroattivo (Backfill ROSS1000)</h4>
          <p className="text-xs text-purple-800 mt-0.5">
            Lo scheduler giornaliero (23:00) invia automaticamente la giornata corrente.
            Per inviare il <strong>pregresso</strong> (es. dal 1° marzo a ieri), avvia qui un job di backfill.
            I giorni già inviati con successo verranno <strong>saltati automaticamente</strong>.
          </p>
        </div>
      </div>

      {/* Job attivo o ultimo job completato */}
      {activeJob && (
        <div className={`mb-3 p-3 rounded border ${
          isRunning ? 'bg-blue-50 border-blue-300' :
          activeJob.status === 'completed' ? 'bg-emerald-50 border-emerald-300' :
          activeJob.status === 'cancelled' ? 'bg-amber-50 border-amber-300' :
          'bg-red-50 border-red-300'
        }`} data-testid="backfill-job-status">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-2">
            <div className="text-sm font-medium flex items-center gap-2">
              {isRunning && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
              {activeJob.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
              {activeJob.status === 'cancelled' && <Square className="h-4 w-4 text-amber-700" />}
              {activeJob.status === 'failed' && <AlertTriangle className="h-4 w-4 text-red-600" />}
              {['pending', 'running'].includes(activeJob.status) && `Job in corso: ${activeJob.processed_days}/${activeJob.total_days} giorni (${progressPct}%)`}
              {activeJob.status === 'cancelling' && 'Interruzione in corso…'}
              {activeJob.status === 'completed' && `Ultimo backfill completato`}
              {activeJob.status === 'cancelled' && 'Backfill interrotto'}
              {activeJob.status === 'failed' && `Ultimo backfill fallito`}
            </div>
            <span className="text-xs text-slate-600">
              {activeJob.from_date} → {activeJob.to_date}
            </span>
          </div>
          {isRunning && (
            <div className="space-y-2 mb-2">
              <div className="w-full bg-white rounded-full h-2 overflow-hidden">
                <div className="bg-blue-600 h-2 transition-[width]" style={{ width: `${progressPct}%` }} />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={cancelBackfill}
                disabled={cancelling || activeJob.status === 'cancelling'}
                className="border-red-500 text-red-700 hover:bg-red-50"
                data-testid="backfill-cancel-btn"
              >
                {cancelling || activeJob.status === 'cancelling' ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Interruzione…</>
                ) : (
                  <><Square className="h-4 w-4 mr-1" /> Interrompi solo questo immobile</>
                )}
              </Button>
            </div>
          )}
          <div className="text-xs text-slate-700 flex flex-wrap gap-x-4 gap-y-1">
            <span>📤 Inviati: <strong>{activeJob.sent_count || 0}</strong></span>
            <span>⏭️ Saltati: <strong>{activeJob.skipped_count || 0}</strong></span>
            <span>❌ Falliti: <strong>{activeJob.failed_count || 0}</strong></span>
            {activeJob.current_date && isRunning && (
              <span>📅 In elaborazione: <strong>{activeJob.current_date}</strong></span>
            )}
          </div>
          {activeJob.errors && activeJob.errors.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-red-700 font-medium">
                {activeJob.errors.length} errori (clicca per dettagli)
              </summary>
              <ul className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                {activeJob.errors.slice(0, 10).map((e, i) => (
                  <li key={i} className="text-red-700">
                    <strong>{e.date}</strong>: {e.msg}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {['cancelled', 'failed'].includes(activeJob.status) && (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className={`text-xs ${activeJob.status === 'failed' ? 'text-red-800' : 'text-amber-800'}`} data-testid="backfill-restart-note">
                {activeJob.status === 'failed'
                  ? 'Il portale non ha risposto. Puoi riprovare dallo stesso periodo quando il collegamento sarà disponibile.'
                  : 'Riparte dal primo giorno del periodo; gli invii già confermati non vengono duplicati.'}
              </p>
              <Button
                type="button"
                size="sm"
                onClick={restartBackfill}
                disabled={submitting}
                className={`${activeJob.status === 'failed' ? 'bg-red-700 hover:bg-red-800' : 'bg-amber-600 hover:bg-amber-700'} text-white shrink-0`}
                data-testid="backfill-restart-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                {activeJob.status === 'failed' ? 'Riprova da zero' : 'Riavvia da zero'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Form per avviare un nuovo job */}
      {!isRunning && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Data Inizio</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={fmt(yesterday)}
              data-testid="backfill-from-date"
            />
          </div>
          <div>
            <Label className="text-xs">Data Fine (max ieri)</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              max={fmt(yesterday)}
              data-testid="backfill-to-date"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={() => startBackfill()}
              disabled={submitting || loading}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="backfill-start-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Avvio...</>
              ) : (
                <><Calendar className="h-4 w-4 mr-1" /> Avvia Backfill</>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ross1000BackfillSection;
